import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ProductCarousel } from "@/components/ProductCarousel";

/**
 * Persistent, context-aware product banner shown under the top menu on every storefront page.
 * - Home / misc pages: all active products, auto drift.
 * - Category page: only that category's products, auto drift.
 * - Product detail page: same-category products with the current product focused (no drift).
 */
export function GlobalProductBanner() {
  const loc = useLocation();
  const [products, setProducts] = useState([]);
  const [mode, setMode] = useState("auto");
  const [focusId, setFocusId] = useState(null);
  const [label, setLabel] = useState("Udforsk vores godter");
  const reqRef = useRef(0);

  useEffect(() => {
    const rid = ++reqRef.current;
    const parts = loc.pathname.split("/").filter(Boolean);
    const set = (p, m, f, l) => {
      if (reqRef.current !== rid) return;
      setProducts(p); setMode(m); setFocusId(f); setLabel(l);
    };

    async function load() {
      try {
        if (parts[0] === "produkter" && parts[1]) {
          const { data: prod } = await api.get(`/products/${parts[1]}`);
          const cat = prod.category_ids?.[0];
          let list = [];
          if (cat) {
            const { data } = await api.get(`/products?category=${cat}&limit=30`);
            list = data.products || [];
          }
          if (!list.find((x) => x.id === prod.id)) list = [prod, ...list];
          set(list, "focus", prod.id, "Fra samme kategori");
        } else if (parts[0] === "kategori" && parts[1]) {
          const { data } = await api.get(`/products?category=${parts[1]}&limit=40`);
          set(data.products || [], "auto", null, "Produkter i kategorien");
        } else {
          const { data } = await api.get(`/products?limit=40`);
          set(data.products || [], "auto", null, "Udforsk vores godter");
        }
      } catch (e) { console.error("Kunne ikke hente produkter til banner:", e); }
    }
    load();
  }, [loc.pathname]);

  if (!products.length) return null;

  return (
    <section
      data-testid="global-product-banner"
      className="relative z-0 w-full border-b border-border bg-gradient-to-b from-[#0d0b12] to-[#15121d] text-white"
    >
      <div className="mx-auto max-w-[1600px] px-2 sm:px-4">
        <div className="flex items-center justify-between px-2 pt-3">
          <p className="lux-eyebrow !text-white/70">{label}</p>
          <span className="hidden text-[11px] text-white/45 sm:block">
            {mode === "focus" ? "Swipe eller brug pilene" : "Før musen til siderne · hold over et produkt"}
          </span>
        </div>
        <ProductCarousel products={products} mode={mode} focusId={focusId} />
      </div>
    </section>
  );
}
